/**
 * RADAR UI Utilities — Global formatting, styling, and navigation helpers
 */

// 1A: Title Case formatter for all status labels, badges, and category names
export function formatLabel(str) {
  if (!str) return '';
  return str
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

// Relative time formatter (e.g., "2 hours ago")
export function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// 1B: Consistent badge class
export const BADGE = 'inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold tracking-wide border';

// Status color maps with borders
export const STATUS_STYLES = {
  draft: `${BADGE} bg-gray-100 text-gray-700 border-gray-200`,
  pending_screening: `${BADGE} bg-yellow-50 text-yellow-700 border-yellow-200`,
  under_review: `${BADGE} bg-blue-50 text-blue-700 border-blue-200`,
  cleared: `${BADGE} bg-green-50 text-green-700 border-green-200`,
  escalated: `${BADGE} bg-red-50 text-red-700 border-red-200`,
  rejected: `${BADGE} bg-red-100 text-red-800 border-red-300`,
  suspended: `${BADGE} bg-orange-50 text-orange-700 border-orange-200`,
};

export const RISK_STYLES = {
  Low: `${BADGE} bg-green-50 text-green-700 border-green-200`,
  Medium: `${BADGE} bg-yellow-50 text-yellow-700 border-yellow-200`,
  High: `${BADGE} bg-orange-50 text-orange-700 border-orange-200`,
  'Very High': `${BADGE} bg-red-50 text-red-700 border-red-200`,
  Prohibited: `${BADGE} bg-red-100 text-red-900 border-red-300`,
};

export const HIT_STATUS_STYLES = {
  pending: `${BADGE} bg-red-50 text-red-700 border-red-200`,
  cleared: `${BADGE} bg-green-50 text-green-700 border-green-200`,
  escalated: `${BADGE} bg-orange-50 text-orange-700 border-orange-200`,
  dismissed: `${BADGE} bg-gray-100 text-gray-600 border-gray-200`,
};

export const ALERT_STATUS_STYLES = {
  pending: `${BADGE} bg-red-100 text-red-700 border-red-300`,
  true_positive: `${BADGE} bg-red-50 text-red-800 border-red-300`,
  false_positive: `${BADGE} bg-green-50 text-green-700 border-green-200`,
  inconclusive: `${BADGE} bg-gray-100 text-gray-600 border-gray-200`,
  whitelisted: `${BADGE} bg-blue-50 text-blue-700 border-blue-200`,
};

// 6A: Human-readable audit action labels
export const ACTION_LABELS = {
  case_created: { label: 'Case Created', icon: 'FileText' },
  case_updated: { label: 'Case Updated', icon: 'PencilSimple' },
  case_deleted: { label: 'Case Deleted', icon: 'Trash' },
  case_transition: { label: 'Status Changed', icon: 'ArrowsClockwise' },
  case_screened: { label: 'Case Screened', icon: 'MagnifyingGlass' },
  screening_performed: { label: 'Screening Performed', icon: 'ShieldCheck' },
  batch_screening: { label: 'Batch Screening', icon: 'ListBullets' },
  risk_calculated: { label: 'Risk Score Calculated', icon: 'Scales' },
  monitoring_toggled: { label: 'Monitoring Toggled', icon: 'Bell' },
  monitoring_hit_resolved: { label: 'Monitoring Alert Resolved', icon: 'CheckCircle' },
  monitoring_manual_trigger: { label: 'Manual Monitoring Run', icon: 'Play' },
  user_login: { label: 'User Logged In', icon: 'Key' },
  user_logout: { label: 'User Logged Out', icon: 'SignOut' },
  user_registered: { label: 'User Registered', icon: 'UserPlus' },
  user_created: { label: 'User Created', icon: 'UserPlus' },
  user_updated: { label: 'User Updated', icon: 'PencilSimple' },
  user_deleted: { label: 'User Deleted', icon: 'Trash' },
  watchlist_created: { label: 'Watchlist Created', icon: 'ListBullets' },
  watchlist_entry_added: { label: 'Watchlist Entry Added', icon: 'Plus' },
  watchlist_entry_removed: { label: 'Watchlist Entry Removed', icon: 'Minus' },
  watchlist_deleted: { label: 'Watchlist Deleted', icon: 'Trash' },
  data_sync_all: { label: 'Full Data Sync', icon: 'ArrowsClockwise' },
  data_sync_source: { label: 'Source Data Sync', icon: 'CloudArrowDown' },
  sync_config_updated: { label: 'Sync Config Updated', icon: 'GearSix' },
  sync_engine_started: { label: 'Sync Engine Started', icon: 'Play' },
  sync_engine_stopped: { label: 'Sync Engine Stopped', icon: 'Stop' },
  match_alert_resolved: { label: 'Match Alert Resolved', icon: 'CheckCircle' },
  incoming_config_created: { label: 'API Source Created', icon: 'Plug' },
  incoming_config_updated: { label: 'API Source Updated', icon: 'PencilSimple' },
  incoming_config_deleted: { label: 'API Source Deleted', icon: 'Trash' },
  incoming_pull: { label: 'Data Pulled from API', icon: 'CloudArrowDown' },
};

// 6B: Human-readable field names for audit diffs
export const FIELD_LABELS = {
  risk_score: 'Risk Score',
  risk_rating: 'Risk Rating',
  monitoring_enabled: 'Monitoring',
  full_name: 'Full Name',
  status: 'Status',
  nationality: 'Nationality',
  case_type: 'Case Type',
  source_of_wealth: 'Source of Wealth',
  country_of_residence: 'Country of Residence',
  date_of_birth: 'Date of Birth',
  id_type: 'ID Type',
  id_number: 'ID Number',
  occupation: 'Occupation',
  notes: 'Notes',
  source: 'Source',
  role: 'Role',
  is_active: 'Active Status',
  name: 'Name',
};

export function formatFieldName(raw) {
  return FIELD_LABELS[raw] || formatLabel(raw);
}

// Risk chart colors
export const RISK_CHART_COLORS = {
  Low: '#34C759',
  Medium: '#FFCC00',
  High: '#FF9500',
  'Very High': '#FF3B30',
  Prohibited: '#8B0000',
};
